/**
 * Bundled sample documents.
 *
 * ## ⚠️ Why these exist, and why the failing one matters most
 *
 * "What do I upload?" is dead air at the start of a demo, and whatever somebody grabs from their
 * downloads folder is unlikely to exercise the interesting case. These fill the whole form in one
 * click and — crucially — **guarantee the failure case gets shown**.
 *
 * That is the one that sells the four-state design. A clean contract produces a green banner that
 * any regex could have produced. The contract with one signature missing is where `pass` /`fail` /
 * `not_applicable` / `needs_review` earns itself: pages 1–2 required nothing, page 3 failed, and a
 * boolean `is_safe` would have called all three unsafe.
 *
 * ## ⚠️ These are SYNTHETIC
 *
 * Generated by `scripts/make-samples.sh` — plain, obviously-fake agreements with real signature
 * blocks. They are not redacted client documents and must never be replaced with any: this bundle
 * ships to whoever the demo is shown to.
 *
 * They are also not a substitute for the accuracy set. A demo proves the product runs; measuring
 * whether the model is RIGHT is separate work against real documents.
 */

export interface Sample {
  id: string
  title: string
  detail: string
  /** Served from `public/samples/`. */
  file: string
  parties: { name: string; role: string }[]
  signingRule: string
}

export const SAMPLES: Sample[] = [
  {
    id: 'clean',
    title: 'Fully signed',
    detail: 'A three-page agreement signed by both parties at the execution page. Expect a pass.',
    file: '/samples/clean-agreement.pdf',
    parties: [
      { name: 'Jane Smith', role: 'Client' },
      { name: 'John Doe', role: 'Supplier' },
    ],
    signingRule: 'full_on_blocks',
  },
  {
    id: 'missing',
    title: 'One signature missing',
    detail:
      'The same agreement with the supplier’s signature line left blank. Expect page 3 to fail — and pages 1–2 to be marked as requiring nothing, not as problems.',
    file: '/samples/missing-signature.pdf',
    parties: [
      { name: 'Jane Smith', role: 'Client' },
      { name: 'John Doe', role: 'Supplier' },
    ],
    signingRule: 'full_on_blocks',
  },
  {
    id: 'initials',
    title: 'Initials expected everywhere',
    detail:
      'The same document checked against a stricter rule: initials on every page as well as full signatures at the block. Expect the body pages to become findings.',
    file: '/samples/clean-agreement.pdf',
    parties: [
      { name: 'Jane Smith', role: 'Client' },
      { name: 'John Doe', role: 'Supplier' },
    ],
    signingRule: 'initials_and_full_on_blocks',
  },
]

/**
 * Fetch a bundled sample as a `File`, so it enters the form through exactly the path a chosen file
 * does — same upload, same reference, same run. A sample that took a shortcut would be a demo of
 * something other than the product.
 */
export async function loadSample(sample: Sample): Promise<File> {
  const res = await fetch(sample.file)
  if (!res.ok) throw new Error(`Could not load the sample document (${res.status}).`)
  const blob = await res.blob()
  return new File([blob], sample.file.split('/').pop() ?? 'sample.pdf', { type: 'application/pdf' })
}
